import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Box,
    Typography,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Divider,
} from '@mui/material';

import { SCUIButton } from '../../../atomic/atoms';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { SCUIContent } from '../../../atomic/organisms';

import { ItemAcceptanceCancellationCommand } from '../../../services/studio/StudioCommand';
import { generateUUID, handleStudioApiResponse } from '../../helper/studio_helper';

import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import DriveFolderUploadIcon from '@mui/icons-material/DriveFolderUpload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileUploadIcon from '@mui/icons-material/FileUpload';

type FileWithStatus = {
    file: File;
    progress: number;
    status: 'idle' | 'uploading' | 'done' | 'error';
};

export function FileManager() {
    const [files, setFiles] = useState<FileWithStatus[]>([]);
    const [totalProgress, setTotalProgress] = useState<number>(0);
    const [completedCount, setCompletedCount] = useState<number>(0);
    const [failedCount, setFailedCount] = useState<number>(0);
    const [isLoadingToDOM, setIsLoadingToDOM] = useState(false);
    const [guuid, setGuuid] = useState('');

    const isLoading = (files || []).some((f) => f.status === 'uploading');
    const isAnyFailed = (files || []).some((f) => f.status === 'error');
    const isAnyIdle = (files || []).some((f) => f.status === 'idle');

    const { t } = useTranslation();

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const folderInputRef = useRef<HTMLInputElement | null>(null);

    const resetUploadState = () => {
        if (files.length > 0) {
            setFiles([]);
            setTotalProgress(0);
            setCompletedCount(0);
            setFailedCount(0);
            setGuuid('');
        }
    };

    const handleInputFiles = async (inputFiles: FileList | null) => {
        if (!inputFiles) return;

        setIsLoadingToDOM(true);

        try {
            const validFiles = Array.from(inputFiles).filter((file) => {
                const ext = file.name.split('.').pop()?.toLowerCase();
                return ext === 'jpg' || ext === 'jpeg';
            });

            const compressedPreviews = await Promise.all(
                validFiles.map(async (file) => {
                    return {
                        file,
                        progress: 0,
                        status: 'idle' as const,
                    };
                })
            );

            setFiles((prev) => [...prev, ...compressedPreviews]);
        } finally {
            setIsLoadingToDOM(false);
        }
    };

    const markUploadResult = (index: number, status: 'done' | 'error', onComplete: (didFail: boolean) => void) => {
        setFiles((prev) =>
            prev.map((file, fileIndex) =>
                fileIndex === index ? { ...file, status, progress: 100 } : file
            )
        );
        onComplete(status === 'error');
    };

    const uploadFileAtIndex = async (i: number, onComplete: (didFail: boolean) => void, customId: string) => {
        const target = files[i];
        setFiles((prev) => prev.map((f, idx) => (idx === i ? { ...f, status: 'uploading', progress: 0 } : f)));

        const formData = new FormData();

        const cleanFileName = target.file.name.split('/').pop() || target.file.name;

        const fileWithoutFolder = new File([target.file], cleanFileName, {
            type: target.file.type,
            lastModified: target.file.lastModified,
        });

        formData.append('File', fileWithoutFolder);
        formData.append('UniqueId', customId);

        handleStudioApiResponse(
            ItemAcceptanceCancellationCommand.UploadImage(formData),
            () => markUploadResult(i, 'done', onComplete),
            () => null,
            t,
            () => markUploadResult(i, 'error', onComplete)
        );
    };

    const simulateUpload = async () => {
        if (files.length === 0) return;
        setTotalProgress(0);
        setCompletedCount(0);
        setFailedCount(0);

        let uploadCompleted = 0;
        let uploadFailed = 0;

        const transferId = generateUUID();
        setGuuid(transferId);

        for (let i = 0; i < files.length; i++) {
            // eslint-disable-next-line no-loop-func
            await new Promise<void>((resolve) => {
                uploadFileAtIndex(
                    i,
                    (didFail) => {
                        uploadCompleted++;
                        if (didFail) uploadFailed++;
                        setCompletedCount(uploadCompleted);
                        setFailedCount(uploadFailed);
                        setTotalProgress(Math.floor((uploadCompleted / files.length) * 100));
                        resolve();
                    },
                    transferId
                );
            });
        }
    };

    const simulateReUpload = async () => {
        if (files.length === 0) return;
        setFiles((prev) => prev.map((f) => (f.status === 'error' ? { ...f, status: 'uploading', progress: 0 } : f)));
        const completed = files.filter((f) => f.status === 'done').length;
        setFailedCount(0);
        let uploadFailed = 0;
        let uploadCompleted = completed;
        setCompletedCount(completed);
        setTotalProgress(Math.floor((completed / files.length) * 100));
        for (let i = 0; i < files.length; i++) {
            if (files?.[i]?.status === 'error') {
                // eslint-disable-next-line no-loop-func
                await uploadFileAtIndex(
                    i,
                    // eslint-disable-next-line no-loop-func
                    (didFail) => {
                        if (didFail) uploadFailed++;
                        uploadCompleted++;
                        setFailedCount(uploadFailed);
                        setCompletedCount(uploadCompleted);
                        setTotalProgress(() => {
                            return Math.floor((uploadCompleted / files.length) * 100);
                        });
                    },
                    guuid
                );
            }
        }
    };

    const retryUpload = (index: number) => {
        const completed = files.filter((f) => f.status === 'done' || f.status === 'error').length;
        const failed = files.filter((f) => f.status === 'error').length;
        setCompletedCount(completed - 1);
        setFailedCount(failed - 1);
        uploadFileAtIndex(
            index,
            (didFail) => {
                setFailedCount(failed + (didFail ? 0 : -1));
                setCompletedCount(completed);
                setTotalProgress(Math.floor((completed / files.length) * 100));
            },
            guuid
        );
    };

    return (
        <SCUIContent>
            <Box sx={{ padding: '16px' }}>
                <SCUIBreadCrumb onlyTitle={t('studio.upload.studioUpload')} />
                <Box>
                    <Box
                        display="flex"
                        gap={2}
                        mb={3}
                        sx={{
                            '& .Mui-disabled': {
                                backgroundColor: '#e0e0e0 !important',
                            },
                        }}
                    >
                        <SCUIButton
                            text={t('general.selectFiles')}
                            type="primary"
                            sizeType="default"
                            size={'s'}
                            disabled={isLoading}
                            onClick={() => {
                                resetUploadState();
                                fileInputRef.current?.click();
                            }}
                            startIcon={<UploadFileIcon sx={{ color: '#fff' }} />}
                            showStartIcon
                        />
                        <SCUIButton
                            text={t('general.selectFolder')}
                            type="primary"
                            sizeType="default"
                            disabled={isLoading}
                            size={'s'}
                            onClick={() => {
                                resetUploadState();
                                folderInputRef.current?.click();
                            }}
                            startIcon={<DriveFolderUploadIcon sx={{ color: '#fff' }} />}
                            showStartIcon
                        />
                        <SCUIButton
                            text={t('studio.upload.startUpload')}
                            style={{ backgroundColor: 'green', borderColor: 'green' }}
                            type="primary"
                            sizeType="default"
                            size={'s'}
                            disabled={files.length === 0 || isLoading || (!isAnyFailed && !isAnyIdle)}
                            onClick={simulateUpload}
                            startIcon={<FileUploadIcon sx={{ color: '#fff' }} />}
                            showStartIcon
                        />
                        {isAnyFailed && (
                            <SCUIButton
                                text={t('studio.upload.reUpload')}
                                style={{ backgroundColor: 'red', borderColor: 'red' }}
                                type="primary"
                                sizeType="default"
                                size={'s'}
                                disabled={files.length === 0 || isLoading}
                                onClick={simulateReUpload}
                                startIcon={<ReplayIcon sx={{ color: '#fff' }} />}
                                showStartIcon
                            />
                        )}
                    </Box>
                    {isLoadingToDOM && (
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <CircularProgress size={18} />
                            <Typography fontSize={14} fontWeight={600}>
                                {t('studio.upload.preparing')}
                            </Typography>
                        </Box>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/jpeg"
                        style={{ display: 'none' }}
                        onChange={(e) => handleInputFiles(e.target.files)}
                    />
                    <input
                        ref={folderInputRef}
                        type="file"
                        multiple
                        accept="image/jpeg"
                        style={{ display: 'none' }}
                        onChange={(e) => handleInputFiles(e.target.files)}
                    />
                    {files?.length > 0 && (
                        <Box mb={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {isLoading && <CircularProgress sx={{ marginRight: '8px' }} size={24} thickness={5} />}
                                <p className="studio-form-label">{t('studio.upload.uploadStatus')}</p>
                            </Box>
                            <Box display="flex" alignItems="center" gap={2}>
                                <LinearProgress variant="determinate" value={totalProgress} sx={{ flex: 1 }} />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        minWidth: 75,
                                        textAlign: 'right',
                                        fontWeight: '600 !important',
                                        fontSize: '14px !important',
                                    }}
                                >
                                    {`${completedCount} / ${files.length} ${t(
                                        'studio.upload.files'
                                    )} (${totalProgress}%)`}
                                    {failedCount > 0 && (
                                        <Typography
                                            component="span"
                                            color="error"
                                            sx={{ ml: 1, fontWeight: '600 !important', fontSize: '14px !important' }}
                                        >
                                            | {failedCount} {t('studio.upload.uploadFailed')}
                                        </Typography>
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <List>
                        {files?.map((f, index) => (
                            <ListItem key={index} alignItems="flex-start">
                                <Box sx={{ width: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', padding: '16px' }}>
                                        <Box sx={{ minWidth: '200px' }}>
                                            <ListItemText
                                                primary={f.file.name}
                                                secondary={`${(f.file.size / 1024).toFixed(2)} KB`}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', marginLeft: '16px', alignItems: 'center' }}>
                                            {f.status === 'uploading' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                                    <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                                                        {t('studio.upload.uploading')}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {f.status === 'done' && (
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <CheckIcon sx={{ color: 'green', marginRight: '4px' }} />
                                                    <p className="studio-form-label">{t('general.success')}</p>
                                                </Box>
                                            )}
                                            {f.status === 'error' && (
                                                <Box display="flex" alignItems="center" gap={4}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <CancelIcon sx={{ color: 'red', marginRight: '4px' }} />
                                                        <p className="studio-form-label">
                                                            {t('studio.upload.uploadFailed')}
                                                        </p>
                                                    </Box>
                                                    <SCUIButton
                                                        text={t('general.reUpload')}
                                                        type="primary"
                                                        sizeType="default"
                                                        size={'s'}
                                                        onClick={() => retryUpload(index)}
                                                        startIcon={<ReplayIcon sx={{ color: '#fff' }} />}
                                                        showStartIcon
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                    {index + 1 !== files.length && <Divider sx={{ width: '100%' }} />}
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Box>
        </SCUIContent>
    );
}
